import {AppleIcon} from '@github-ui/custom-icons/AppleIcon'
import {LinuxIcon} from '@github-ui/custom-icons/LinuxIcon'
import {WindowsIcon} from '@github-ui/custom-icons/WindowsIcon'
import {sendEvent} from '@github-ui/hydro-analytics'
import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup, IconButton, Label, LinkButton} from '@primer/react'
import {useMemo} from 'react'

import styles from './CopilotAppBanner.module.css'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export type CopilotAppDownloadPlatform = 'mac' | 'windows' | 'linux'

export interface CopilotAppDownloadItem {
  id: string
  label: string
  platform: CopilotAppDownloadPlatform
  href: string
  analyticsAction: string
}

export interface CopilotAppBannerAnalytics {
  category: string
  label: string
  location: string
}

export interface CopilotAppBannerPayload {
  label: string
  title: string
  dismissPath: string
  analytics: CopilotAppBannerAnalytics
  download: {
    label: string
    href: string
    analyticsAction: string
    menuAnalyticsAction: string
    items: CopilotAppDownloadItem[]
  }
  testSelector: string
}

export interface CopilotAppBannerProps {
  banner: CopilotAppBannerPayload
  rounded?: boolean
}

const ICON_BY_PLATFORM: Record<CopilotAppDownloadPlatform, Icon> = {
  mac: AppleIcon,
  windows: WindowsIcon,
  linux: LinuxIcon,
}

function analyticsLabel(analytics: CopilotAppBannerAnalytics) {
  return `${analytics.label}location:${analytics.location};`
}

function analyticsEvent(analytics: CopilotAppBannerAnalytics, action: string) {
  return JSON.stringify({
    category: analytics.category,
    action,
    label: analyticsLabel(analytics),
  })
}

export function CopilotAppBanner({banner, rounded = true}: CopilotAppBannerProps) {
  const {analytics, download} = banner

  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'CopilotAppBanner',
      surface: 'banner',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: analytics.category,
      variant: 'download',
    }),
    [analytics.category, analytics.location],
  )

  return (
    <IpmBannerBase
      dismissPath={banner.dismissPath}
      visibleEvent={analyticsEvent(analytics, 'visible')}
      dismissEvent={analyticsEvent(analytics, 'dismiss')}
      testId={banner.testSelector}
      title="GitHub Copilot app"
      location={analytics.location}
      rounded={rounded}
      hideLeadingVisual
      className={styles.neutralSurface}
      telemetryTags={telemetryTags}
      description={
        <span className={styles.description}>
          <Label variant="success" className={styles.label}>
            {banner.label}
          </Label>
          <span className={styles.text}>{banner.title}</span>
        </span>
      }
      primaryAction={
        <ButtonGroup>
          <LinkButton
            href={download.href}
            target="_blank"
            rel="noopener noreferrer"
            data-analytics-event={analyticsEvent(analytics, download.analyticsAction)}
            onClick={() => reportIpmAction('cta_click', telemetryTags)}
          >
            {download.label}
          </LinkButton>
          <ActionMenu
            onOpenChange={open => {
              // The trigger also closes an open menu, so a declarative data-analytics-event here
              // would record an "opened" action on the way back out too.
              if (!open) return
              reportIpmAction('click', {...telemetryTags, variant: 'download_menu'})
              sendEvent('analytics.click', {
                category: analytics.category,
                action: download.menuAnalyticsAction,
                label: analyticsLabel(analytics),
              })
            }}
          >
            <ActionMenu.Anchor>
              <IconButton icon={TriangleDownIcon} aria-label="Choose a GitHub Copilot app download" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay align="end">
              <ActionList>
                {download.items.map(item => {
                  const ItemIcon = ICON_BY_PLATFORM[item.platform]
                  return (
                    <ActionList.LinkItem
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={item.id}
                      data-analytics-event={analyticsEvent(analytics, item.analyticsAction)}
                      onClick={() => reportIpmAction('cta_click', {...telemetryTags, variant: item.platform})}
                    >
                      <ActionList.LeadingVisual>
                        <ItemIcon />
                      </ActionList.LeadingVisual>
                      {item.label}
                    </ActionList.LinkItem>
                  )
                })}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ButtonGroup>
      }
    />
  )
}
